import {SectionView} from '../../ui_canvas_engine/SectionView'
import {PIXI} from '@colonist/pixi'
import {SpriteView} from '../../ui_canvas_engine/SpriteView'
import {EndGameStateUtils} from '@colonist/game-endgame-types'
import {UIGameEndStatsController} from './UIGameEndStatsController'
import {TranslationObject} from '@colonist/i18n'
import {Sprites} from '../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {VictoryPointDataController, VictoryPointTypes} from '@colonist/game-base-types'
import {EndGameState} from '@colonist/game-states'
import {UIGameManager} from '../UIGameManager'
import {navigateToPage} from '../../ui_utils/ClientUtils.ts'
import {ImageURL} from '@colonist/types'
import {VICTORY_POINT_END_GAME_TIP, VICTORY_POINT_IMAGES} from '@colonist/ui-game'

export class UIGameEndTableHeadView extends SectionView {
    private readonly uiGameManager: UIGameManager

    private readonly itemSize: number
    private readonly endGameState: EndGameState

    private readonly overviewHeader: SectionView
    private readonly activityStatsHeader: SectionView
    private readonly resourceStatsHeader: SectionView

    private headerNowX: number = 0
    private readonly columnWidth: number

    constructor(uiGameManager: UIGameManager, x: number, y: number, w: number, h: number, container: PIXI.Container, columnWidth: number, endGameStatsController: UIGameEndStatsController, endGameState: EndGameState) {
        super(x, y, w, h, container)
        this.uiGameManager = uiGameManager
        this.itemSize = endGameStatsController.itemSize
        this.columnWidth = columnWidth
        this.endGameState = endGameState

        this.createTrophy()
        this.overviewHeader = new SectionView(this.columnWidth, 0, w, h, this)
        this.activityStatsHeader = new SectionView(this.columnWidth, 0, w, h, this)
        this.resourceStatsHeader = new SectionView(this.columnWidth, 0, w, h, this)

        this.createOverviewHeader()
        this.createActivityStatsHeader()
        this.createResourceStatsHeader()
        this.showOverviewHeader()
    }

    private createActivityStatsHeader() {
        const headerTextures = [UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_proposed_trades), UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_successful_trades), UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_resources_used),
            UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_resource_income_blocked), UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_dev_card_bought), UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_dev_card_used)]

        const devCardsBoughtTitle: TranslationObject = {
            key: 'strings:game.endGame.activity.devCardsBought',
            options: {context: this.uiGameManager.gameState.gameModeData.translationKeyContext},
        }
        const devCardsUsedTitle: TranslationObject = {
            key: 'strings:game.endGame.activity.devCardsUsed',
            options: {context: this.uiGameManager.gameState.gameModeData.translationKeyContext},
        }
        const headerHoverText: TranslationObject[] = [
            {key: `strings:game.endGame.activity.proposedTrades`},
            {key: `strings:game.endGame.activity.successfulTrades`},
            {key: `strings:game.endGame.activity.resourcesUsed`},
            {key: `strings:game.endGame.activity.resourceIncomeBlocked`},
            devCardsBoughtTitle,
            devCardsUsedTitle,
        ]

        this.createHeaders(headerTextures, headerHoverText, this.activityStatsHeader)
        this.activityStatsHeader.visible = false
    }

    private createResourceStatsHeader() {
        const headerTextures = [UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_res_gain), UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_res_loss), UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_res_score),
            UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_rolling_income), UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_robbing_income), UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_dev_card_income),
            UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_trade_income), UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_rolling_loss), UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_robbing_loss),
            UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_dev_card_loss), UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_trade_loss), UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_gold_income)]

        const devCardIncomeTitle: TranslationObject = {
            key: 'strings:game.endGame.resource.devCardIncome',
            options: {context: this.uiGameManager.gameState.gameModeData.translationKeyContext},
        }
        const devCardLossTitle: TranslationObject = {
            key: 'strings:game.endGame.resource.devCardLoss',
            options: {context: this.uiGameManager.gameState.gameModeData.translationKeyContext},
        }
        const headerHoverText: TranslationObject[] = [
            {key: `strings:game.endGame.resource.totalResourceIncome`},
            {key: `strings:game.endGame.resource.totalResourceLoss`},
            {key: `strings:game.endGame.resource.totalResourceScore`},
            {key: `strings:game.endGame.resource.rollingIncome`},
            {key: `strings:game.endGame.resource.robbingIncome`},
            devCardIncomeTitle,
            {key: `strings:game.endGame.resource.tradeIncome`},
            {key: `strings:game.endGame.resource.rollingLoss`},
            {key: `strings:game.endGame.resource.robbingLoss`},
            devCardLossTitle,
            {key: `strings:game.endGame.resource.tradeLoss`},
            {key: `strings:game.endGame.resource.goldIncome`},
        ]

        this.createHeaders(headerTextures, headerHoverText, this.resourceStatsHeader)
    }

    private static getEndGameVictoryPointImages(victoryPointTypes: VictoryPointTypes[]): ImageURL[] {
        return victoryPointTypes.map((victoryPoint) => VICTORY_POINT_IMAGES[victoryPoint])
    }

    private createOverviewHeader() {
        const uniqueVictoryPointsEarnedThisGame = EndGameStateUtils.getAllUniqueVictoryPointsEarnedByAllPlayers(this.endGameState.players)
        const headerTextures: PIXI.Texture[] = []
        for(const uniqueVictoryPoint of UIGameEndTableHeadView.getEndGameVictoryPointImages(uniqueVictoryPointsEarnedThisGame)) {
            headerTextures.push(UIGameSpriteHandler.getTextureFromSpritesheet(uniqueVictoryPoint))
        }

        const headerHoverTextKeys: TranslationObject[] = this.getEndGameVictoryPointToolTips(uniqueVictoryPointsEarnedThisGame)

        this.createHeaders(headerTextures, headerHoverTextKeys, this.overviewHeader)
        if(this.uiGameManager.gameController.gameDetails.isRanked) this.createStatMMR()
    }

    // eslint-disable-next-line class-methods-use-this
    private getEndGameVictoryPointToolTips(victoryPointTypes: VictoryPointTypes[]): TranslationObject[] {
        const endGameImageToolTips: TranslationObject[] = []
        for(const victoryPoint of VictoryPointDataController.victoryPointDataList) {
            if(endGameImageToolTips.includes({key: VICTORY_POINT_END_GAME_TIP[victoryPoint.victoryPointType]})) continue
            if(!victoryPointTypes.includes(victoryPoint.victoryPointType)) continue
            endGameImageToolTips.push({key: VICTORY_POINT_END_GAME_TIP[victoryPoint.victoryPointType]})
        }
        return endGameImageToolTips
    }

    private createHeaders(textures: PIXI.Texture[], hoverTexts: TranslationObject[], container: SectionView) {
        this.headerNowX = 0
        for(const [index, texture] of textures.entries()) {
            const item = new SpriteView(this.headerNowX, 0, this.itemSize, this.itemSize, container, texture, this.itemSize * 0.1)

            const itemTitle: TranslationObject = {key: 'strings:colonistCommon.utils.emptyString'}
            const itemHoverText = hoverTexts[index]
            item.setTip(itemTitle, [itemHoverText])
            this.headerNowX += this.columnWidth
        }
    }

    private createStatMMR() {
        const statTextureMMR = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.stat_mmr_change)
        const statImageMMR = new SpriteView(this.headerNowX, 0, this.itemSize, this.itemSize, this.overviewHeader, statTextureMMR, this.itemSize * 0.1)

        const statImageMMRTipTitle: TranslationObject = {key: 'strings:colonistCommon.utils.emptyString'}
        const statImageMMRTipBody: TranslationObject = {key: 'strings:game.endGame.overview.mmrChange'}
        statImageMMR.setTip(statImageMMRTipTitle, [statImageMMRTipBody])

        statImageMMR.setClickAction(() => {
            navigateToPage('/leaderboards', true)
        })
    }

    private createTrophy() {
        const trophyTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.icon_trophy)
        const trophyImage = new SpriteView(0, 0, this.itemSize, this.itemSize, this, trophyTexture)
        const trophyImageTipTitle: TranslationObject = {key: 'strings:game.endGame.icons.trophy'}
        const trophyImageTipBody: TranslationObject = {key: 'strings:colonistCommon.utils.emptyString'}
        trophyImage.setTip(trophyImageTipTitle, [trophyImageTipBody])
    }

    hideHeaders() {
        this.overviewHeader.visible = false
        this.activityStatsHeader.visible = false
        this.resourceStatsHeader.visible = false
    }

    showOverviewHeader() {
        this.hideHeaders()
        this.overviewHeader.visible = true
    }

    showActivityStatsHeader() {
        this.hideHeaders()
        this.activityStatsHeader.visible = true
    }

    showResourceStatsHeader() {
        this.hideHeaders()
        this.resourceStatsHeader.visible = true
    }
}
